/**
 * Nav-item visibility — the single predicate every navigation surface uses.
 *
 * A `NavItem` can carry its own gates (`requiresAdmin`, `requiresSysadmin`,
 * `requiresGoogleConnection`, `requiresMicrosoftConnection`,
 * `requiresForwarderMode`) that are INDEPENDENT of its module's gate: an
 * admin-only page can live inside a module every employee may open. The sidebar,
 * the module picker and global search must all apply that gate identically —
 * they previously did not, so search offered pages the sidebar hid — hence one
 * exported predicate rather than a filter expression per surface.
 *
 * Note the deliberate difference from the MODULE-level gate in
 * `getVisibleModules` (modules.config.tsx): there a sysadmin satisfies
 * `requiresAdmin`, because a sysadmin must be able to reach admin modules. At
 * ITEM level there is no such fallback — `is_admin` and `is_sysadmin` are
 * independent flags (`is_sysadmin` derives from the tenant type), and the
 * sidebar has always gated item-level `requiresAdmin` on `isAdmin` alone. The
 * two levels are not to be unified.
 *
 * This is result LISTING, not authorization: route guards and API authz remain
 * the enforcement point.
 */

import type { NavItem } from '../config/modules.config';

/** Everything the nav-item gates are evaluated against, resolved per viewer. */
export interface NavItemAccess {
  /** `user.is_admin` — tenant admin. NOT implied by sysadmin at item level. */
  isAdmin: boolean;
  /** `user.is_sysadmin` — derived from the tenant type, independent of admin. */
  isSysadmin: boolean;
  /** Google account connected (Gmail / Drive nav items). */
  isGoogleConnected: boolean;
  /** Microsoft account connected (Outlook / OneDrive nav items). */
  isMicrosoftConnected: boolean;
  /** Tenant persona flag `sells_freight_services` (Forwarder TMS nav items). */
  sellsFreightServices: boolean;
}

/** Whether this viewer may see (and navigate to) the given nav item. */
export function isNavItemVisible(navItem: NavItem, access: NavItemAccess): boolean {
  if (navItem.requiresGoogleConnection && !access.isGoogleConnected) return false;
  if (navItem.requiresMicrosoftConnection && !access.isMicrosoftConnected) return false;
  if (navItem.requiresAdmin && !access.isAdmin) return false;
  if (navItem.requiresSysadmin && !access.isSysadmin) return false;
  if (navItem.requiresForwarderMode && !access.sellsFreightServices) return false;
  return true;
}
